import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Descriptions, Form, Input, Layout, Modal, Select, Space, Table, Tag, message } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import organizationService from '../services/organizationService';
import roleService from '../services/roleService';
import userService from '../services/userService';
import { canCreateUser, canDeleteUser, canEditUser, canManageUsers, isSuperAdmin } from '../utils/permissions';

function Users() {
  const [form] = Form.useForm();
  const { user } = useSelector((state) => state.auth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(user?.organizationId || undefined);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const effectiveOrganizationId = useMemo(
    () => (isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId),
    [selectedOrganizationId, user]
  );

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        if (isSuperAdmin(user)) {
          const organizationsResponse = await organizationService.getAll(1, 1000);
          setOrganizations(organizationsResponse.data || []);
        }
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [user]);

  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const response = await userService.getAll(1, 100, effectiveOrganizationId ? { organizationId: effectiveOrganizationId } : {});
        setUsers(response.data || []);
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadUsers();
    }
  }, [effectiveOrganizationId, user]);

  useEffect(() => {
    const loadRoles = async () => {
      if (!effectiveOrganizationId) {
        setRoles([]);
        return;
      }

      try {
        const response = await roleService.getAll(1, 100, { organizationId: effectiveOrganizationId });
        setRoles(response.data || []);
      } catch (error) {
        message.error(error.response?.data?.message || 'Failed to load roles');
      }
    };

    loadRoles();
  }, [effectiveOrganizationId]);

  const handleCreateUser = async (values) => {
    setLoading(true);
    try {
      const payload = {
        organizationId: isSuperAdmin(user) ? values.organizationId : user?.organizationId,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || null,
        roleId: values.roleId,
        isActive: values.isActive ?? true
      };

      if (modalMode === 'edit' && selectedUser) {
        const response = await userService.update(selectedUser.id, payload);
        setUsers((prev) => prev.map((existingUser) => (existingUser.id === selectedUser.id ? response.data : existingUser)));
        message.success('User updated successfully');
      } else {
        const response = await userService.create(payload);
        setUsers((prev) => [response.data, ...prev]);
        message.success(`User created successfully. Default password: ${response.meta?.defaultPassword || 'generated'}`);
      }

      form.resetFields();
      setIsModalOpen(false);
      setSelectedUser(null);
      setModalMode('create');
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (id) => {
    try {
      setLoading(true);
      const response = await userService.getById(id);
      const userData = response?.data?.data || response?.data;
      setSelectedUser(userData || null);
      setIsViewModalOpen(true);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (id) => {
    try {
      setLoading(true);
      const response = await userService.getById(id);
      const userData = response?.data?.data || response?.data;
      setSelectedUser(userData || null);
      setModalMode('edit');
      form.setFieldsValue({
        organizationId: userData?.organizationId,
        firstName: userData?.firstName,
        lastName: userData?.lastName,
        email: userData?.email,
        phone: userData?.phone,
        roleId: userData?.roleId,
        isActive: userData?.isActive
      });
      setIsModalOpen(true);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (id) => {
    Modal.confirm({
      title: 'Delete User',
      content: 'Are you sure you want to delete this user?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          setLoading(true);
          await userService.delete(id);
          setUsers((prev) => prev.filter((existingUser) => existingUser.id !== id));
          message.success('User deleted successfully');
        } catch (error) {
          message.error(error.response?.data?.message || 'Failed to delete user');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Organization',
      key: 'organization',
      render: (_, record) => record.organization?.name || '-'
    },
    {
      title: 'Role',
      key: 'role',
      render: (_, record) => <Tag color="blue">{record.role?.name || record.role?.roleType || '-'}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value) => <Tag color={value ? 'green' : 'red'}>{value ? 'Active' : 'Inactive'}</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewUser(record.id)} />
          {canEditUser(user) && <Button type="link" icon={<EditOutlined />} onClick={() => handleEditUser(record.id)} />}
          {canDeleteUser(user) && <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteUser(record.id)} />}
        </Space>
      )
    }
  ];

  return (
    <Layout style={{ padding: '20px' }}>
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ marginBottom: 4 }}>Users</h1>
            <p style={{ margin: 0, color: '#667085' }}>
              Default password is auto-created as first name + last name.
            </p>
          </div>
          {canCreateUser(user) && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setModalMode('create');
                setSelectedUser(null);
                form.resetFields();
                form.setFieldsValue({
                  organizationId: isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId,
                  isActive: true
                });
                setIsModalOpen(true);
              }}
            >
              Add User
            </Button>
          )}
        </div>

        {!canManageUsers(user) && (
          <Alert type="info" message="This role has read-only access to users." />
        )}

        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {isSuperAdmin(user) && (
              <Select
                allowClear
                placeholder="Filter by organization"
                value={selectedOrganizationId}
                onChange={setSelectedOrganizationId}
                options={organizations.map((organization) => ({
                  label: organization.name,
                  value: organization.id
                }))}
                style={{ width: 320 }}
              />
            )}
            <Table rowKey="id" columns={columns} dataSource={users} loading={loading} pagination={{ pageSize: 10 }} />
          </Space>
        </Card>
      </Space>

      <Modal
        title="User Details"
        open={isViewModalOpen}
        footer={null}
        onCancel={() => {
          setIsViewModalOpen(false);
          setSelectedUser(null);
        }}
      >
        {selectedUser && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Name">{`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || '-'}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedUser.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selectedUser.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="Organization">{selectedUser.organization?.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="Role">{selectedUser.role?.name || selectedUser.role?.roleType || '-'}</Descriptions.Item>
            <Descriptions.Item label="Status">{selectedUser.isActive ? 'Active' : 'Inactive'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        title={modalMode === 'edit' ? 'Edit User' : 'Create User'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
          setModalMode('create');
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={modalMode === 'edit' ? 'Update User' : 'Create User'}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateUser}
          initialValues={{
            organizationId: isSuperAdmin(user) ? selectedOrganizationId : user?.organizationId
          }}
        >
          {isSuperAdmin(user) && (
            <Form.Item
              label="Organization"
              name="organizationId"
              rules={[{ required: true, message: 'Please select organization' }]}
            >
              <Select
                placeholder="Select organization"
                options={organizations.map((organization) => ({
                  label: organization.name,
                  value: organization.id
                }))}
              />
            </Form.Item>
          )}

          <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: 'Please enter first name' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: 'Please enter last name' }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Phone" name="phone">
            <Input />
          </Form.Item>
          <Form.Item label="Role" name="roleId" rules={[{ required: true, message: 'Please select role' }]}>
            <Select
              placeholder="Select role"
              options={roles.map((role) => ({
                label: `${role.name} (${role.roleType})`,
                value: role.id
              }))}
            />
          </Form.Item>
          <Form.Item label="Status" name="isActive">
            <Select
              options={[
                { label: 'Active', value: true },
                { label: 'Inactive', value: false }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default Users;
